/**
 * leadFirstTouch — client seam for the deployed `sendLeadFirstTouch` callable
 * (Mack, avbp_lead_first_touch.js). Impure: lazy-imports firebase like
 * emailSend.ts / adminActionQueue.ts so pure modules stay SDK-free.
 *
 * The callable is the EMAIL lane of first touch. It reads the lead's email +
 * name server-side, sends ONE Brevo transactional email from the business
 * identity (care@aayatanveda.in), is idempotent (refuses a second send unless
 * `force`), and appends a `leads/{id}/communications` first_touch entry. So the
 * portal only supplies the leadId (plus optional custom subject/body/force).
 *
 * Contract (server-locked):
 *   input  { leadId, subject?, body?, force? }
 *   return { ok, emailDispatch: 'sent' | 'already_sent' | 'failed', messageId?, reason? }
 *   throws HttpsError — unauthenticated / permission-denied (not a team
 *          session), invalid-argument, not-found, failed-precondition (no
 *          valid email on the lead → use the WhatsApp lane).
 */

export interface FirstTouchInput {
  leadId: string;
  subject?: string;
  body?: string;
  force?: boolean;
}

export interface FirstTouchResult {
  ok: boolean;
  emailDispatch: 'sent' | 'already_sent' | 'failed';
  messageId?: string | null;
  reason?: string;
}

/** Send (or re-send with `force`) the business-identity first-touch email.
 *  Throws on an HttpsError (auth / not-found / no-email) — callers surface it. */
export async function sendLeadFirstTouch(
  input: FirstTouchInput,
): Promise<FirstTouchResult> {
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('./firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<FirstTouchInput, FirstTouchResult>(
    functions,
    'sendLeadFirstTouch',
  );
  const res = await fn(input);
  return res.data;
}
